import { onDropNode, useDndNode, useDropLine } from '@platejs/dnd'
import { isEntityRef } from '@tunasong/schemas'
import type { CoreElement } from '@tunasong/schemas'
import type { FC } from 'react'
import { useCallback, useRef } from 'react'
import { Editor } from 'slate'
import { ReactEditor, useReadOnly, useSlateStatic } from 'slate-react'
import type { CoreBlockProps } from './core-block.js'
import { CoreBlock } from './core-block.js'

const DndEnabledBlock: FC<CoreBlockProps> = props => {
  const nodeRef = useRef<HTMLDivElement>(null)
  const editor = useSlateStatic()

  /** Keep a stable ref to the current element so the drop handler callback
   *  doesn't need props.element as a dependency (which changes every edit). */
  const elementRef = useRef(props.element)
  elementRef.current = props.element

  /** Custom drop handler that resolves the dragged element by ID instead of
   *  relying on the stale element reference stored by useDragNode. Without this,
   *  findPath(dragItem.element) fails because Slate replaces element objects on
   *  re-render, causing moveNodes to use editor.selection as the path — which
   *  moves the wrong block.
   *  Also handles elements inside ref wrappers (e.g. stored images at [5,0])
   *  by resolving the ref parent for correct path calculation. */
  const onDropHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dropEditor: any, { dragItem, monitor }: { id: string; dragItem: any; monitor: any; nodeRef: any }) => {
      // Resolve the drag element fresh by ID so findPath works
      const dragId = dragItem.id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const freshEntry = (editor as any).api.node({ id: dragId, at: [] })
      if (freshEntry) {
        dragItem.element = freshEntry[0]
      }

      // Resolve drop target: use ref parent for ref-wrapped elements
      const currentElement = elementRef.current
      let dropElement: CoreElement = currentElement
      try {
        const path = ReactEditor.findPath(editor as unknown as ReactEditor, currentElement)
        if (path.length > 1) {
          const [parent, parentPath] = Editor.parent(editor, path)
          if (isEntityRef(parent as CoreElement) && parentPath.length === 1) {
            dropElement = parent as CoreElement
          }
        }
      } catch {
        // findPath may fail during DnD DOM transitions — proceed with
        // currentElement as-is so onDropNode can still attempt the move
        // using Plate's internal path resolution.
      }

      onDropNode(dropEditor, {
        dragItem,
        element: dropElement,
        monitor,
        nodeRef,
      })
      return true
    },
    [editor]
  )

  const { dragRef, isOver: isDragOver } = useDndNode({
    nodeRef,
    element: props.element,
    onDropHandler,
  })

  /** Reflect Plate's actual hover direction so the drop indicator agrees with
   *  where the block will land (top vs bottom). The hardcoded 'top' previously
   *  here lied for bottom-half drops. */
  const { dropLine } = useDropLine({ id: props.element.id })

  return (
    <CoreBlock
      {...props}
      dropLine={dropLine}
      dragRef={dragRef}
      blockRef={nodeRef}
      isDragOver={isDragOver}
    />
  )
}

export const DndBlock: FC<CoreBlockProps> = props => {
  const readOnly = useReadOnly()

  if (readOnly) {
    return <CoreBlock {...props} />
  }

  return <DndEnabledBlock {...props} />
}
